import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppConfigProvider, useAppConfig } from "./context/AppConfigContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import Layout from "./components/layout/Layout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ProfileEditorPage from "./pages/ProfileEditorPage";
import AccountPage from "./pages/AccountPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import OrganizationDetailPage from "./pages/OrganizationDetailPage";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import ProfileStatsPage from "./pages/ProfileStatsPage";
import ProfileCrmPage from "./pages/ProfileCrmPage";
import CardsPage from "./pages/CardsPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import UpgradePage from "./pages/UpgradePage";
import PublicProfilePage from "./pages/PublicProfilePage";
import CardRedirectPage from "./pages/CardRedirectPage";
import DistributorLoginPage from "./pages/DistributorLoginPage";
import DistributorDashboardPage from "./pages/DistributorDashboardPage";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function AppRoutes() {
  const { enableI18n } = useAppConfig();

  return (
    <LanguageProvider enabled={enableI18n}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/distributor/login" element={<DistributorLoginPage />} />
            <Route path="/distributor" element={<DistributorDashboardPage />} />
            <Route path="/c/:code" element={<CardRedirectPage />} />
            <Route path="/p/:slug" element={<PublicProfilePage />} />
            <Route
              path="/app"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/app/profiles" replace />} />
              <Route path="profiles" element={<DashboardPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route path="org/:orgId" element={<OrganizationDetailPage />} />
              <Route path="org/:orgId/template" element={<TemplateEditorPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="upgrade" element={<UpgradePage />} />
              <Route path="profile/:slug" element={<ProfileEditorPage />} />
              <Route path="profile/:slug/stats" element={<ProfileStatsPage />} />
              <Route path="profile/:slug/crm" element={<ProfileCrmPage />} />
            </Route>
            <Route
              path="/app/preview/:slug"
              element={
                <PrivateRoute>
                  <PublicProfilePage />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/app" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

function App() {
  return (
    <AppConfigProvider>
      <AppRoutes />
    </AppConfigProvider>
  );
}

export default App;
